/**
 * MySQL Compatibility Client with Dual-Mode Direct/Proxy Support
 * This file replaces the original supabaseClient.ts to seamlessly connect either:
 * 1. To the local Express development server (in dev environment)
 * 2. Directly to the SQL API proxy inside api.php (in production, so the compiled dist/ directory is completely serverless and self-contained!)
 */

const JSON_BACKED_TABLES = ['daily_closings', 'deferred_points', 'approved_invoices', 'flexible_offers', 'agent_orders', 'expenses'];

const WAREHOUSE_ORDERS_COL_MAP: Record<string, string> = {
  id: 'COL 1',
  agentId: 'COL 2',
  warehouseId: 'COL 3',
  date: 'COL 4',
  items: 'COL 5',
  totalPoints: 'COL 6',
  createdAt: 'COL 7',
  status: 'COL 8',
  agent_id: 'COL 9',
  created_by: 'COL 10',
  warehouse_id: 'COL 11',
  total_points: 'COL 12',
  type: 'COL 13',
  customer_name: 'COL 14',
  customer_phone: 'COL 15',
  customer_membership: 'COL 16',
  total_price_usd: 'COL 17',
  exchange_rate: 'COL 18',
  notes: 'COL 19',
  member_id: 'COL 20',
  offer_id: 'COL 21',
  user_name: 'COL 22',
  center_name: 'COL 23',
  is_backdated: 'COL 24',
  backdated_status: 'COL 25',
  user_id: 'COL 26'
};

const REVERSE_ORDERS_WAREHOUSES_MAP: Record<string, string> = {};
Object.entries(WAREHOUSE_ORDERS_COL_MAP).forEach(([k, v]) => {
  REVERSE_ORDERS_WAREHOUSES_MAP[v] = k;
});

function mapRowFromDB(row: any) {
  if (!row || typeof row !== 'object') return row;
  if (row['COL 1'] === undefined && row.id !== undefined) return row; // already mapped/standard keys
  const mapped: any = {};
  Object.entries(row).forEach(([colName, val]) => {
    const stdKey = REVERSE_ORDERS_WAREHOUSES_MAP[colName] || colName;
    mapped[stdKey] = val;
  });
  return mapped;
}

function unpackRow(row: any) {
  if (!row) return row;
  let parsed: any = {};
  if (row.value && typeof row.value === 'string') {
    try {
      parsed = JSON.parse(row.value);
    } catch (e) {
      console.error("Failed to parse JSON value for row:", row.id, e);
    }
  }
  const result = {
    ...parsed,
    id: row.id !== undefined && row.id !== null ? String(row.id) : parsed.id,
    created_at: row.created_at || parsed.created_at
  };
  delete result.value;
  return result;
}

function packRow(data: any) {
  const idValue = data.id || Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  const createdAtValue = data.created_at || data.createdAt || new Date().toISOString().slice(0, 19).replace('T', ' ');
  
  const payload = { ...data };
  delete payload.id;
  delete payload.created_at;
  delete payload.createdAt;
  
  return {
    id: String(idValue),
    created_at: createdAtValue,
    value: JSON.stringify(payload)
  };
}

function formatRawPayload(data: any): any {
  if (!data || typeof data !== 'object') return data;
  if (Array.isArray(data)) {
    return data.map(formatRawPayload);
  }
  const formatted: any = {};
  for (const key of Object.keys(data)) {
    const value = data[key];
    if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
      formatted[key] = JSON.stringify(value);
    } else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/.test(value)) {
      // Safely convert ISO string (e.g. 2026-06-20T12:00:00.000Z) to MySQL format (2026-06-20 12:00:00)
      formatted[key] = value.substring(0, 19).replace('T', ' ');
    } else {
      formatted[key] = value;
    }
  }
  return formatted;
}

function escapeSqlValue(val: any): string {
  if (val === null || val === undefined) return 'NULL';
  if (typeof val === 'boolean') return val ? '1' : '0';
  if (typeof val === 'number') return String(val);
  if (val instanceof Date) {
    return `'${val.toISOString().slice(0, 19).replace('T', ' ')}'`;
  }
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/.test(val)) {
    return `'${val.substring(0, 19).replace('T', ' ')}'`;
  }
  if (typeof val === 'object') {
    return `'${String(JSON.stringify(val)).replace(/'/g, "''")}'`;
  }
  return `'${String(val).replace(/'/g, "''")}'`;
}

function escapeSqlIdentifier(ident: string): string {
  return `\`${ident.replace(/`/g, '``')}\``;
}

const getApiPhpUrl = () => {
  return "https://www.basseraa.com/api.php";
};

const isBrowserProduction = () => {
  if (typeof window === 'undefined') return true;
  return !window.location.hostname.includes('localhost') && 
         !window.location.hostname.includes('127.0.0.1') && 
         !window.location.hostname.includes('run.app');
};

const getStorageBaseUrl = () => {
  return 'https://www.basseraa.com/uploads';
};

const fetchUploadApiPhp = async (formData: FormData): Promise<Response> => {
  const candidateUrls: string[] = [
    "https://www.basseraa.com/api.php"
  ];

  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    const originUrl = `${window.location.origin}/api.php`;
    if (!candidateUrls.includes(originUrl)) {
      candidateUrls.push(originUrl);
    }
  }

  let lastResponse: Response | null = null;
  let lastError: any = null;

  for (const url of candidateUrls) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        body: formData
      });

      if (res.ok) {
        return res;
      }

      if ([404, 405, 502, 503, 504].includes(res.status)) {
        lastResponse = res;
        continue;
      }

      return res;
    } catch (err: any) {
      lastError = err;
    }
  }

  if (lastResponse) return lastResponse;
  throw lastError || new Error("فشل رفع الملف إلى السيرفر.");
};

const fetchApiPhp = async (body: any): Promise<Response> => {
  const candidateUrls: string[] = [
    "https://www.basseraa.com/api.php"
  ];

  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    const originUrl = `${window.location.origin}/api.php`;
    if (!candidateUrls.includes(originUrl)) {
      candidateUrls.push(originUrl);
    }
  }

  let lastResponse: Response | null = null;
  let lastError: any = null;

  for (const url of candidateUrls) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        return res;
      }

      // If status is 405 (Method Not Allowed) or 404 (Not Found) or 502/503/504, try next candidate
      if ([404, 405, 502, 503, 504].includes(res.status)) {
        lastResponse = res;
        continue;
      }

      return res;
    } catch (err: any) {
      lastError = err;
    }
  }

  if (lastResponse) return lastResponse;
  throw lastError || new Error("Failed to connect to database API.");
};

class MySQLQueryBuilder {
  private table: string;
  private filters: Record<string, any> = {};
  private selectColumns: string = '*';
  private orderBy: string = '';
  private limitCount: number | null = null;
  private method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET';
  private bodyData: any = null;

  constructor(table: string) {
    this.table = table;
  }

  select(columns: string = '*') {
    // Only set method to GET if it hasn't been set to a mutation (POST, PUT, DELETE)
    if (this.method === 'GET') {
      this.method = 'GET';
      this.selectColumns = columns;
    }
    return this;
  }

  insert(data: any | any[]) {
    this.method = 'POST';
    this.bodyData = data;
    return this;
  }

  update(data: any) {
    this.method = 'PUT';
    this.bodyData = data;
    return this;
  }

  upsert(data: any | any[], options?: any) {
    this.method = 'POST'; // Map upsert to POST natively
    this.bodyData = data;
    return this;
  }

  delete() {
    this.method = 'DELETE';
    return this;
  }

  eq(column: string, value: any) {
    this.filters[column] = value;
    return this;
  }

  neq(column: string, value: any) {
    this.filters[`${column}!not`] = value;
    return this;
  }

  not(column: string, operator: string, value: any) {
    if (operator === 'is' && value === null) {
      this.filters[`${column}!is_null`] = true;
    } else {
      this.filters[`${column}!not`] = value;
    }
    return this;
  }

  is(column: string, value: any) {
    if (value === null) {
      this.filters[`${column}!is_null`] = false;
    }
    return this;
  }

  match(filters: Record<string, any>) {
    Object.assign(this.filters, filters);
    return this;
  }

  like(column: string, pattern: string) {
    this.filters[`${column}!like`] = pattern;
    return this;
  }

  gt(column: string, value: any) {
    this.filters[`${column}!gt`] = value;
    return this;
  }

  in(column: string, values: any[]) {
    this.filters[`${column}!in`] = values.join(',');
    return this;
  }

  order(column: string, options: { ascending?: boolean } = { ascending: true }) {
    if (this.orderBy) this.orderBy += ',';
    this.orderBy += `${column}.${options.ascending ? 'asc' : 'desc'}`;
    return this;
  }

  limit(count: number) {
    this.limitCount = count;
    return this;
  }

  async maybeSingle() {
    const { data, error } = await this.execute();
    return { data: (data && Array.isArray(data) && data.length > 0) ? data[0] : null, error };
  }

  async single() {
    const { data, error } = await this.execute();
    return { data: (data && Array.isArray(data) && data.length > 0) ? data[0] : null, error };
  }

  private async execute(retryCount = 0): Promise<{ data: any, error: any }> {
    try {
      if (this.table === 'orders_warehouses') {
        // Map selectColumns if not default
        if (this.selectColumns !== '*') {
          this.selectColumns = this.selectColumns.split(',').map(col => {
            const trimmed = col.trim();
            return WAREHOUSE_ORDERS_COL_MAP[trimmed] || trimmed;
          }).join(', ');
        }
        
        // Map filters
        const newFilters: Record<string, any> = {};
        Object.entries(this.filters).forEach(([fKey, fVal]) => {
          const parts = fKey.split('!');
          const baseCol = parts[0];
          const mappedCol = WAREHOUSE_ORDERS_COL_MAP[baseCol] || baseCol;
          parts[0] = mappedCol;
          newFilters[parts.join('!')] = fVal;
        });
        this.filters = newFilters;
        
        // Map orderBy
        if (this.orderBy) {
          this.orderBy = this.orderBy.split(',').map(part => {
            const parts = part.split('.');
            const baseCol = parts[0];
            const mappedCol = WAREHOUSE_ORDERS_COL_MAP[baseCol] || baseCol;
            parts[0] = mappedCol;
            return parts.join('.');
          }).join(',');
        }
        
        // Map bodyData for inserts/updates
        if (this.bodyData) {
          const mapRowToDB = (row: any) => {
            if (!row || typeof row !== 'object') return row;
            const mapped: any = {};
            Object.entries(row).forEach(([k, v]) => {
              const mappedKey = WAREHOUSE_ORDERS_COL_MAP[k] || k;
              let val = v;
              if (k === 'items' && typeof v === 'object' && v !== null) {
                val = JSON.stringify(v);
              }
              mapped[mappedKey] = val;
            });
            return mapped;
          };
          
          if (Array.isArray(this.bodyData)) {
            this.bodyData = this.bodyData.map(mapRowToDB);
          } else {
            this.bodyData = mapRowToDB(this.bodyData);
          }
        }
      }

      const isProduction = isBrowserProduction();

      if (isProduction) {
        // --- PRODUCTION CLIENT-SIDE DIRECT QUERY EXECUTION via api.php ---
        const apiPhpUrl = getApiPhpUrl();
        const apiSecret = 'B@SSERAA_SECURE_API_2026!';
        let sqlQuery = '';

        if (this.method === 'GET') {
          // SQL query building
          const escapedSelectCols = this.selectColumns === '*' 
            ? '*' 
            : this.selectColumns.split(',').map(col => {
                const trimmed = col.trim();
                if (trimmed.startsWith('`') && trimmed.endsWith('`')) return trimmed;
                if (trimmed.includes('(') || trimmed.includes(')')) return trimmed;
                return escapeSqlIdentifier(trimmed);
              }).join(', ');
          let queryHead = `SELECT ${escapedSelectCols} FROM ${escapeSqlIdentifier(this.table)}`;
          
          if (JSON_BACKED_TABLES.includes(this.table)) {
             // JSON backed tables only have id, created_at, and value cols.
             // We cannot apply WHERE or arbitrary ORDER BY unless we parse JSON, 
             // but currently filtering happens in memory for these in the frontend StoreContext.
             if (this.orderBy) {
                queryHead += ` ORDER BY created_at DESC`;
             }
             if (this.limitCount !== null) {
                queryHead += ` LIMIT ${this.limitCount}`;
             }
             sqlQuery = queryHead;
          } else {
            let conditions: string[] = [];
            Object.entries(this.filters).forEach(([key, value]) => {
              if (key.endsWith('!is_null')) {
                const col = key.replace('!is_null', '');
                conditions.push(`${escapeSqlIdentifier(col)} ${value === 'true' ? 'IS NOT' : 'IS'} NULL`);
              } else if (key.endsWith('!not')) {
                const col = key.replace('!not', '');
                conditions.push(`${escapeSqlIdentifier(col)} != ${escapeSqlValue(value)}`);
              } else if (key.endsWith('!like')) {
                const col = key.replace('!like', '');
                conditions.push(`${escapeSqlIdentifier(col)} LIKE ${escapeSqlValue(value)}`);
              } else if (key.endsWith('!gt')) {
                const col = key.replace('!gt', '');
                conditions.push(`${escapeSqlIdentifier(col)} > ${escapeSqlValue(value)}`);
              } else if (key.endsWith('!in')) {
                const col = key.replace('!in', '');
                const valuesList = String(value).split(',').map(v => escapeSqlValue(v)).join(', ');
                conditions.push(`${escapeSqlIdentifier(col)} IN (${valuesList})`);
              } else {
                conditions.push(`${escapeSqlIdentifier(key)} = ${escapeSqlValue(value)}`);
              }
            });

            if (conditions.length > 0) {
              queryHead += ` WHERE ${conditions.join(' AND ')}`;
            }

            if (this.orderBy) {
              const orderParts = this.orderBy.split(',').map(part => {
                const [col, dir] = part.split('.');
                return `${escapeSqlIdentifier(col)} ${dir === 'desc' ? 'DESC' : 'ASC'}`;
              });
              queryHead += ` ORDER BY ${orderParts.join(', ')}`;
            }

            if (this.limitCount !== null) {
              queryHead += ` LIMIT ${this.limitCount}`;
            }

            sqlQuery = queryHead;
          }
        } else if (this.method === 'POST') {
          const rowsToInsert = Array.isArray(this.bodyData) ? this.bodyData : [this.bodyData];
          if (rowsToInsert.length === 0) return { data: [], error: null };

          const formattedRows = rowsToInsert.map(row => {
            if (JSON_BACKED_TABLES.includes(this.table)) {
              return packRow(row);
            }
            return formatRawPayload(row);
          });

          const keys = Object.keys(formattedRows[0]);
          const columnsStr = keys.map(escapeSqlIdentifier).join(', ');
          const valuesStr = formattedRows.map(row => {
            return '(' + keys.map(k => escapeSqlValue(row[k])).join(', ') + ')';
          }).join(', ');

          sqlQuery = `INSERT INTO ${escapeSqlIdentifier(this.table)} (${columnsStr}) VALUES ${valuesStr}`;
          
          const updateCols = keys.filter(k => k !== 'id' && k !== 'key');
          if (updateCols.length > 0) {
            const updateStr = updateCols.map(k => `${escapeSqlIdentifier(k)} = VALUES(${escapeSqlIdentifier(k)})`).join(', ');
            sqlQuery += ` ON DUPLICATE KEY UPDATE ${updateStr}`;
          }
        } else if (this.method === 'PUT') {
          const formattedData = JSON_BACKED_TABLES.includes(this.table) ? packRow(this.bodyData) : formatRawPayload(this.bodyData);
          const setParts = Object.entries(formattedData).map(([k, v]) => {
            return `${escapeSqlIdentifier(k)} = ${escapeSqlValue(v)}`;
          });
          let queryHead = `UPDATE ${escapeSqlIdentifier(this.table)} SET ${setParts.join(', ')}`;

          let conditions: string[] = [];
          Object.entries(this.filters).forEach(([key, value]) => {
            if (key.endsWith('!is_null')) {
              const col = key.replace('!is_null', '');
              conditions.push(`${escapeSqlIdentifier(col)} ${value === 'true' ? 'IS NOT' : 'IS'} NULL`);
            } else if (key.endsWith('!not')) {
              const col = key.replace('!not', '');
              conditions.push(`${escapeSqlIdentifier(col)} != ${escapeSqlValue(value)}`);
            } else {
              conditions.push(`${escapeSqlIdentifier(key)} = ${escapeSqlValue(value)}`);
            }
          });

          if (conditions.length > 0) {
            queryHead += ` WHERE ${conditions.join(' AND ')}`;
          }
          sqlQuery = queryHead;
        } else if (this.method === 'DELETE') {
          let queryHead = `DELETE FROM ${escapeSqlIdentifier(this.table)}`;
          let conditions: string[] = [];
          Object.entries(this.filters).forEach(([key, value]) => {
            if (key.endsWith('!is_null')) {
              const col = key.replace('!is_null', '');
              conditions.push(`${escapeSqlIdentifier(col)} ${value === 'true' ? 'IS NOT' : 'IS'} NULL`);
            } else if (key.endsWith('!not')) {
              const col = key.replace('!not', '');
              conditions.push(`${escapeSqlIdentifier(col)} != ${escapeSqlValue(value)}`);
            } else {
              conditions.push(`${escapeSqlIdentifier(key)} = ${escapeSqlValue(value)}`);
            }
          });

          if (conditions.length > 0) {
            queryHead += ` WHERE ${conditions.join(' AND ')}`;
          }
          sqlQuery = queryHead;
        }

        let response;
        try {
          response = await fetchApiPhp({
            secret: apiSecret,
            query_base64: btoa(unescape(encodeURIComponent(sqlQuery)))
          });
        } catch (netErr: any) {
          console.error("Database proxy query fetch error:", netErr);
          throw new Error("خطأ في الاتصال بقاعدة البيانات. يرجى التحقق من اتصال الإنترنت.");
        }

        if (!response.ok) {
          let errMsg = `PHP Proxy responded: ${response.statusText} (${response.status})`;
          try {
            const errJson = await response.json();
            if (errJson && errJson.error) errMsg = errJson.error;
          } catch (e) {}
          throw new Error(errMsg);
        }

        const responseJSON = await response.json();
        if (responseJSON.error) {
          throw new Error(responseJSON.error);
        }

        if (this.method === 'GET') {
          let rows = responseJSON.data || [];
          if (JSON_BACKED_TABLES.includes(this.table)) {
            rows = rows.map(unpackRow);
          }
          if (this.table === 'orders_warehouses') {
            rows = rows.filter((r: any) => r['COL 1'] !== 'id').map(mapRowFromDB);
          }
          return { data: rows, error: null };
        } else {
          const fakeResult = {
            id: responseJSON.insert_id || (this.bodyData ? (this.bodyData.id || this.bodyData.key) : null),
            status: "success"
          };
          const resultData = this.method === 'POST' && Array.isArray(this.bodyData) ? [fakeResult] : fakeResult;
          return { data: resultData, error: null };
        }
      }

      // --- DEVELOPMENT CLIENT-SIDE EXECUTION (Classic Node.js Backend Routing) ---
      const queryParams = new URLSearchParams();
      if (this.method === 'GET') {
        if (this.selectColumns !== '*') queryParams.append('select', this.selectColumns);
        if (this.orderBy) queryParams.append('order', this.orderBy);
        if (this.limitCount) queryParams.append('limit', this.limitCount.toString());
      }
      
      Object.entries(this.filters).forEach(([k, v]) => queryParams.append(k, v));

      const url = `/api/db/${this.table}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      const options: RequestInit = {
        method: this.method,
        headers: {}
      };

      if (this.method !== 'GET' && this.bodyData) {
        options.headers = { 'Content-Type': 'application/json' };
        options.body = JSON.stringify(this.bodyData);
      }

      const response = await fetch(url, options);
      const responseText = await response.text();
      
      let data: any;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        const isStartupPage = responseText.includes('<title>Starting Server...</title>') || responseText.includes('Rate exceeded');
        
        if (!isStartupPage) {
          console.error(`❌ Non-JSON response from ${url}:`, responseText.substring(0, 200));
        }
        
        if (isStartupPage && retryCount < 12) {
          const waitTime = Math.min(2000 + (retryCount * 1000), 10000);
          console.log(`⏳ Platform is activating backend, retrying in ${waitTime/1000}s... (attempt ${retryCount + 1})`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return this.execute(retryCount + 1);
        }

        return { 
          data: null, 
          error: { 
            message: isStartupPage 
              ? "The backend is still activating. Please wait a few more seconds and refresh."
              : `Server returned non-JSON response (${response.status}).`,
            code: isStartupPage ? 'SERVER_STARTING' : 'INVALID_JSON'
          } 
        };
      }
      
      let resultData = data;
      if (this.method === 'POST') {
        resultData = Array.isArray(this.bodyData) ? [data] : data;
      }

      if (this.table === 'orders_warehouses' && this.method === 'GET' && Array.isArray(resultData)) {
        resultData = resultData.filter((r: any) => r['COL 1'] !== 'id').map(mapRowFromDB);
      }

      return { data: resultData, error: response.ok ? null : { message: data.error || data.details || 'Server error', code: data.code } };
    } catch (error: any) {
      if ((error.message === 'Failed to fetch' || error.message?.includes('NetworkError')) && retryCount < 5) {
        const waitTime = 1000 + (retryCount * 1000);
        console.warn(`⚠️ Network error (Failed to fetch), retrying in ${waitTime/1000}s... (attempt ${retryCount + 1})`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        return this.execute(retryCount + 1);
      }
      console.error("MySQL Mock Execute Error:", error);
      return { data: null, error: { message: error.message } };
    }
  }

  then(onfulfilled?: any, onrejected?: any) {
    return this.execute().then(onfulfilled, onrejected);
  }
}

export const supabase: any = {
  from: (table: string) => new MySQLQueryBuilder(table),
  storage: {
    from: (bucket: string) => {
      // Normalize bucket names to clean folders
      let normalizedBucket = bucket || 'system';
      if (normalizedBucket === 'product-images' || normalizedBucket === 'product_images' || normalizedBucket === 'products') normalizedBucket = 'products';
      else if (normalizedBucket === 'user-assets' || normalizedBucket === 'user_assets' || normalizedBucket === 'users' || normalizedBucket === 'employees') normalizedBucket = 'employees';
      else if (normalizedBucket === 'attendance-photos' || normalizedBucket === 'attendance_photos' || normalizedBucket === 'attendance') normalizedBucket = 'attendance';
      else if (normalizedBucket === 'auth_images' || normalizedBucket === 'offer-members' || normalizedBucket === 'members') normalizedBucket = 'members';
      else if (normalizedBucket === 'videos') normalizedBucket = 'videos';
      else if (normalizedBucket === 'uploads' || normalizedBucket === 'system_assets' || normalizedBucket === 'system') normalizedBucket = 'system';

      return {
        upload: async (path: string, file: File | Blob, options?: any) => {
          console.log("MySQL Storage Upload:", normalizedBucket, path);
          try {
            const isProduction = isBrowserProduction();

            const formData = new FormData();
            formData.append('bucket', normalizedBucket);
            formData.append('path', path);
            formData.append('file', file);

            let response: Response;
            if (isProduction) {
              formData.append('secret', 'B@SSERAA_SECURE_API_2026!');
              response = await fetchUploadApiPhp(formData);
            } else {
              response = await fetch('/api/storage/upload', {
                method: 'POST',
                body: formData
              });
            }

            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`Upload failed: ${errorText}`);
            }

            const result = await response.json();
            const cleanPath = result.path || `${normalizedBucket}/${path}`;
            const publicUrl = result.publicUrl || result.url || `${getStorageBaseUrl()}/${cleanPath}`;
            return { data: { path: cleanPath, publicUrl }, error: null };
          } catch (error: any) {
            console.error("Storage Upload Error:", error);
            return { data: null, error: { message: error.message } };
          }
        },
        getPublicUrl: (path: string) => {
          let clean = (path || '').trim();
          if (!clean) return { data: { publicUrl: '' } };

          // If already full url
          if (clean.startsWith('http://') || clean.startsWith('https://')) {
            // Fix double uploads/uploads/
            clean = clean.replace(/\/uploads\/uploads\//g, '/uploads/');
            return { data: { publicUrl: clean } };
          }

          // Strip redundant prefixes
          clean = clean.replace(/^\/+/, '');
          while (clean.toLowerCase().startsWith('uploads/')) {
            clean = clean.substring(8);
          }

          // Check if clean already contains bucket
          const knownFolders = ['system', 'employees', 'attendance', 'products', 'members', 'videos'];
          const hasFolder = knownFolders.some(kf => clean.startsWith(`${kf}/`));
          if (!hasFolder && normalizedBucket) {
            clean = `${normalizedBucket}/${clean}`;
          }

          const isProduction = isBrowserProduction();

          const baseUrl = isProduction ? getStorageBaseUrl() : '/uploads';
          return { data: { publicUrl: `${baseUrl}/${clean}` } };
        },
        list: async (path?: string, options?: any) => ({ data: [], error: null }),
        remove: async (paths: string[]) => ({ data: [], error: null })
      };
    }
  },
  auth: {
    getSession: async () => ({ data: { session: null }, error: null }),
    signInWithPassword: async ({ email, password }: any) => {
      try {
        const isProduction = isBrowserProduction();

        if (isProduction) {
          const apiSecret = 'B@SSERAA_SECURE_API_2026!';
          const emailLower = String(email).trim().toLowerCase();

          // 1. Primary check on profiles
          const primarySql = `SELECT * FROM ${escapeSqlIdentifier('profiles')} WHERE LOWER(${escapeSqlIdentifier('email')}) = ${escapeSqlValue(emailLower)} AND ${escapeSqlIdentifier('password')} = ${escapeSqlValue(password)} LIMIT 1`;
          let responsePrimary;
          try {
            responsePrimary = await fetchApiPhp({ secret: apiSecret, query_base64: btoa(unescape(encodeURIComponent(primarySql))) });
          } catch (netErr: any) {
            console.error("Login Primary fetch error:", netErr);
            throw new Error("خطأ في الاتصال بالخادم أثناء تسجيل الدخول. يرجى التحقق من اتصال الإنترنت.");
          }
          
          if (!responsePrimary.ok) {
            let errorText = "فشل التحقق من بيانات الدخول في الخادم.";
            try {
              const errJson = await responsePrimary.json();
              if (errJson && errJson.error) {
                errorText = `خطأ في الخادم أو قاعدة البيانات: ${errJson.error}`;
              }
            } catch (jsonErr) {}
            throw new Error(errorText);
          }
          const primaryRes = await responsePrimary.json();
          if (primaryRes.data && primaryRes.data.length > 0) {
            return { data: { user: primaryRes.data[0], session: { access_token: 'direct-token' } }, error: null };
          }

          // 2. Secondary check on auth_users
          const secondarySql = `SELECT * FROM ${escapeSqlIdentifier('auth_users')} WHERE LOWER(${escapeSqlIdentifier('email')}) = ${escapeSqlValue(emailLower)} LIMIT 1`;
          let responseSecondary;
          try {
            responseSecondary = await fetchApiPhp({ secret: apiSecret, query_base64: btoa(unescape(encodeURIComponent(secondarySql))) });
          } catch (netErr: any) {
            console.error("Login Secondary fetch error:", netErr);
            throw new Error("خطأ في الاتصال بالخادم.");
          }
          
          if (responseSecondary.ok) {
            const secondaryRes = await responseSecondary.json();
            if (secondaryRes.data && secondaryRes.data.length > 0) {
              const authUser = secondaryRes.data[0];
              
              // See if profile already exists
              const profileCheckSql = `SELECT * FROM ${escapeSqlIdentifier('profiles')} WHERE LOWER(${escapeSqlIdentifier('email')}) = ${escapeSqlValue(emailLower)} LIMIT 1`;
              const responseProfCheck = await fetchApiPhp({ secret: apiSecret, query_base64: btoa(unescape(encodeURIComponent(profileCheckSql))) });
              
              if (responseProfCheck.ok) {
                const profCheckRes = await responseProfCheck.json();
                if (profCheckRes.data && profCheckRes.data.length > 0) {
                  const user = profCheckRes.data[0];
                  if (password && (!user.password || user.password !== password)) {
                    // Update password
                    const updatePassSql = `UPDATE ${escapeSqlIdentifier('profiles')} SET ${escapeSqlIdentifier('password')} = ${escapeSqlValue(password)} WHERE ${escapeSqlIdentifier('id')} = ${escapeSqlValue(user.id)}`;
                    await fetchApiPhp({ secret: apiSecret, query_base64: btoa(unescape(encodeURIComponent(updatePassSql))) });
                    user.password = password;
                  }
                  return { data: { user, session: { access_token: 'direct-token' } }, error: null };
                } else {
                  // Dynamically create a profile record within profiles
                  const idValue = authUser.id;
                  const phoneValue = authUser.phone || "";
                  const nameValue = (() => {
                    const normalized = emailLower;
                    if (normalized.startsWith("ma5zan.ba")) return "مستودع بغداد (BA)";
                    if (normalized.startsWith("ma5zan.er")) return "مستودع أربيل (ER)";
                    if (normalized.startsWith("basra")) return "مستودع البصرة";
                    if (normalized.startsWith("dora")) return "مستودع الدورة";
                    if (normalized.startsWith("maysan")) return "مستودع ميسان";
                    if (normalized.startsWith("nasria")) return "مستودع الناصرية";
                    if (normalized.startsWith("falogeh")) return "مستودع الفلوجة";
                    const [username] = normalized.split("@");
                    return username.charAt(0).toUpperCase() + username.slice(1);
                  })();
                  const roleValue = (emailLower.includes("admin") || emailLower.includes("samer")) ? "SUPER_ADMIN" : "VIEWER";

                  const insertProfSql = `INSERT INTO ${escapeSqlIdentifier('profiles')} (${escapeSqlIdentifier('id')}, ${escapeSqlIdentifier('email')}, ${escapeSqlIdentifier('name')}, ${escapeSqlIdentifier('password')}, ${escapeSqlIdentifier('role')}, ${escapeSqlIdentifier('phone')}) VALUES (${escapeSqlValue(String(idValue))}, ${escapeSqlValue(emailLower)}, ${escapeSqlValue(nameValue)}, ${escapeSqlValue(password)}, ${escapeSqlValue(roleValue)}, ${escapeSqlValue(phoneValue)})`;
                  await fetchApiPhp({ secret: apiSecret, query_base64: btoa(unescape(encodeURIComponent(insertProfSql))) });

                  const createdUser = {
                    id: String(idValue),
                    email: emailLower,
                    name: nameValue,
                    password: password,
                    role: roleValue,
                    phone: phoneValue
                  };
                  return { data: { user: createdUser, session: { access_token: 'direct-token' } }, error: null };
                }
              }
            }
          }

          return { data: { user: null, session: null }, error: { message: "اسم المستخدم أو كلمة المرور غير صحيحة" } };
        }

        // --- DEVELOPMENT AUTH LOGIN (Classic Node.js Backend Routing) ---
        const response = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password })
        });
        const responseText = await response.text();
        let result: any;
        try {
          result = JSON.parse(responseText);
        } catch (e) {
          return { data: { user: null, session: null }, error: { message: "Invalid server response (Auth)" } };
        }

        if (response.ok) {
          return { data: { user: result.user, session: { access_token: 'mock-token' } }, error: null };
        } else {
          return { data: { user: null, session: null }, error: result.error };
        }
      } catch (error: any) {
        return { data: { user: null, session: null }, error };
      }
    },
    signOut: async () => {
      const isProduction = !window.location.hostname.includes('localhost') && 
                           !window.location.hostname.includes('127.0.0.1') && 
                           !window.location.hostname.includes('run.app');
      if (!isProduction) {
        await fetch('/api/auth/session', { method: 'DELETE' });
      }
      return { error: null };
    },
    onAuthStateChange: (callback: any) => ({ data: { subscription: { unsubscribe: () => {} } } }),
    refreshSession: async () => ({ data: { session: null }, error: null })
  },
  channel: (name: string) => {
    const channelObj: any = {
      on: (event: string, filter: any, callback: any) => {
        return channelObj;
      },
      subscribe: (cb?: any) => ({})
    };
    return channelObj;
  },
  removeChannel: async (channel: any) => ({})
};
